import { InAppState } from "../base/InAppState";
import template from "./PushBroadcastTemplate.html";
import { _lcs, locale } from "../../i18n";

/**
 * PushBroadcast — "Push-Nachrichten" (sidebar group "Guests"): the platform
 * notifies the guests of every house, or of a chosen set of houses.
 *
 * Web Push from core-api (no OneSignal). A guest's subscription belongs to the
 * house it was made on, so a broadcast is "every house sends this to its own
 * guests": it arrives under each house's name and icon. GET /push/broadcast
 * gives the reach in total and per house plus the platform's own sends;
 * POST /push/broadcast sends. What hosts send to their own house (client-admin
 * "Push-Nachrichten") is not listed here.
 */
export class PushBroadcast extends InAppState
{
    static ROUTE = "/guest/push";
    static ID = "guest-push";

    // Mirrors core-api WebPushService.LIMITS.
    static LIMITS = { title : 120, body : 500 };

    getContentHeaderData()
    {
        return {
            title : _lcs( "Push-Nachrichten" ),
            subtitle : _lcs( "Benachrichtigungen an die Gäste aller Unterkünfte" )
        };
    }

    /** Where a tap on the notification leads — routes of the guest-app. */
    static targets()
    {
        return [
            { url : "/",                  label : _lcs( "Startseite der Gäste-App" ) },
            { url : "/#/info",            label : _lcs( "Gästemappe" ) },
            { url : "/#/events",          label : _lcs( "Veranstaltungen" ) },
            { url : "/#/recommendations", label : _lcs( "Empfehlungen" ) },
            { url : "/#/region",          label : _lcs( "Region entdecken" ) },
            { url : "/#/plan",            label : _lcs( "Aufenthaltsplaner" ) },
            { url : "/#/chat",            label : _lcs( "Chat" ) }
        ];
    }

    async enter()
    {
        this.sending = false;
        const root = this.app.view.pageContent;
        root.innerHTML = '<p class="pb-message">' + esc( _lcs( "Lade Push-Nachrichten …" ) ) + "</p>";

        const data = await this.load();
        if ( !this.isActive() )
        {
            return;
        }
        if ( !data )
        {
            root.innerHTML = '<p class="pb-message">' +
                esc( _lcs( "Die Push-Nachrichten konnten nicht geladen werden. Bitte versuche es später erneut." ) ) + "</p>";
            return;
        }
        this.render( data );
    }

    async load()
    {
        try
        {
            const { result } = await this.app.api.get( "/push/broadcast" );
            return result && +result.status === 200 ? result.data : null;
        }
        catch ( e )
        {
            console.error( e );
            return null;
        }
    }

    render( data )
    {
        const root = this.app.view.pageContent;
        const dateFormat = new Intl.DateTimeFormat( locale(), { dateStyle : "medium", timeStyle : "short" } );
        this.houses = data.houses || [];
        this.totalDevices = data.devices || 0;

        this.app.view.render( root, template, {
            enabled : !!data.enabled,
            devices : this.totalDevices,
            users : data.users || 0,
            houses : this.houses,
            housesWithDevices : this.houses.filter( ( h ) => h.devices > 0 ).length,
            messages : ( data.messages || [] ).map( ( m ) => ( {
                ...m,
                date : m.createdAt ? dateFormat.format( new Date( m.createdAt ) ) : "",
                audienceLabel : m.audience && Array.isArray( m.audience.accommodationIds )
                    ? _lcs( "{count} Unterkünfte", { count : m.audience.accommodationIds.length } )
                    : _lcs( "Alle Unterkünfte" )
            } ) ),
            limits : PushBroadcast.LIMITS,
            targets : PushBroadcast.targets()
        } );

        this.form = root.querySelector( '[data-id="pb-form"]' );
        this.addEventListener( this.form, "input", ( e ) => this.onInput( e ) );
        this.addEventListener( this.form, "change", () => this.updateAudience() );
        this.addEventListener( this.form, "submit", ( e ) =>
        {
            e.preventDefault();
            this.send();
        } );
        this.addEventListener( this.form, "click", ( e ) => this.onClick( e ) );

        this.updatePreview();
        this.updateAudience();
    }

    onInput( e )
    {
        if ( e.target.matches( '[data-id="pb-search"]' ) )
        {
            const term = e.target.value.trim().toLowerCase();
            this.form.querySelectorAll( ".pb-houses__item" ).forEach( ( li ) =>
            {
                li.hidden = !!term && !li.getAttribute( "data-name" ).includes( term );
            } );
            return;
        }
        this.updatePreview();
    }

    onClick( e )
    {
        const pickReachable = e.target.closest( '[data-id="pb-pick-reachable"]' );
        const pickNone = e.target.closest( '[data-id="pb-pick-none"]' );
        if ( !pickReachable && !pickNone )
        {
            return;
        }
        this.form.querySelectorAll( 'input[name="house"]' ).forEach( ( box ) =>
        {
            box.checked = !!pickReachable && +box.getAttribute( "data-devices" ) > 0;
        } );
        this.updateAudience();
    }

    /** The chosen houses, or null for every house. */
    selectedHouses()
    {
        if ( this.form.querySelector( 'input[name="audience"]:checked' ).value === "all" )
        {
            return null;
        }
        return [ ...this.form.querySelectorAll( 'input[name="house"]:checked' ) ].map( ( box ) => ( {
            id : box.value,
            devices : +box.getAttribute( "data-devices" ) || 0
        } ) );
    }

    /** Show / hide the house list and say how many devices the choice reaches. */
    updateAudience()
    {
        const selected = this.selectedHouses();
        const list = this.form.querySelector( '[data-id="pb-houses"]' );
        list.hidden = selected === null;

        const reach = selected === null
            ? this.totalDevices
            : selected.reduce( ( sum, h ) => sum + h.devices, 0 );
        if ( selected !== null )
        {
            this.form.querySelector( '[data-id="pb-picked"]' ).textContent =
                _lcs( "{count} Unterkünfte ausgewählt.", { count : selected.length } );
        }
        this.form.querySelector( '[data-id="pb-reach"]' ).textContent =
            _lcs( "Erreicht bis zu {count} Geräte", { count : reach } );
        this.reach = reach;
        this._showError( "" );
    }

    updatePreview()
    {
        const title = this.form.querySelector( "#pb-title" ).value;
        const body = this.form.querySelector( "#pb-body" ).value;
        this.form.querySelector( '[data-id="pb-count-title"]' ).textContent = String( title.length );
        this.form.querySelector( '[data-id="pb-count-body"]' ).textContent = String( body.length );
        this.form.querySelector( '[data-id="pb-preview-title"]' ).textContent = title.trim() || _lcs( "Titel der Nachricht" );
        this.form.querySelector( '[data-id="pb-preview-body"]' ).textContent = body.trim() || _lcs( "Hier steht dein Text." );
        this._showError( "" );
    }

    async send()
    {
        if ( this.sending )
        {
            return;
        }

        const title = this.form.querySelector( "#pb-title" ).value.trim();
        const body = this.form.querySelector( "#pb-body" ).value.trim();
        const url = this.form.querySelector( "#pb-url" ).value;
        const selected = this.selectedHouses();

        if ( !title || !body )
        {
            return this._showError( _lcs( "Bitte gib einen Titel und eine Nachricht ein." ) );
        }
        if ( selected !== null && !selected.length )
        {
            return this._showError( _lcs( "Bitte wähle mindestens eine Unterkunft aus." ) );
        }

        // A broadcast cannot be called back — ask once, with its size.
        const ok = await this.app.view.confirm(
            _lcs( "Nachricht an die Gäste senden?" ),
            selected === null
                ? _lcs( "Die Nachricht geht sofort an alle {count} Geräte aller Unterkünfte. Das lässt sich nicht zurücknehmen.", { count : this.reach } )
                : _lcs( "Die Nachricht geht sofort an {count} Geräte in {houses} Unterkünften. Das lässt sich nicht zurücknehmen.",
                    { count : this.reach, houses : selected.length } ),
            _lcs( "Senden" )
        );
        if ( !ok || !this.isActive() )
        {
            return;
        }

        const payload = { title, body, url };
        if ( selected !== null )
        {
            payload.accommodationIds = selected.map( ( h ) => h.id );
        }

        this._setSending( true );
        try
        {
            const { result } = await this.app.api.post( "/push/broadcast", payload );
            const stats = result && result.data ? result.data : {};
            if ( !this.isActive() )
            {
                return;
            }

            if ( !stats.recipients )
            {
                this.app.view.toast( _lcs( "Kein Gerät hat Benachrichtigungen eingeschaltet — die Nachricht ging an niemanden." ), "info", 7000 );
            }
            else if ( stats.failed )
            {
                this.app.view.toast( _lcs( "{sent} von {count} Geräten erreicht, {failed} fehlgeschlagen.",
                    { sent : stats.sent || 0, count : stats.recipients, failed : stats.failed } ), "warning", 7000 );
            }
            else
            {
                this.app.view.toast( _lcs( "Nachricht an {count} Geräte gesendet.", { count : stats.sent || 0 } ), "success", 6000 );
            }

            const data = await this.load();
            if ( data && this.isActive() )
            {
                this.render( data );
            }
        }
        catch ( e )
        {
            console.error( e );
            if ( this.isActive() )
            {
                this._showError( +e?.status === 400
                    ? _lcs( "Bitte überprüfe Titel und Nachricht." )
                    : _lcs( "Die Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut." ) );
            }
        }
        finally
        {
            if ( this.isActive() && this.form )
            {
                this._setSending( false );
            }
        }
    }

    _setSending( on )
    {
        this.sending = on;
        const btn = this.form.querySelector( '[data-id="pb-send"]' );
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    _showError( message )
    {
        const el = this.form && this.form.querySelector( '[data-id="pb-error"]' );
        if ( el )
        {
            el.textContent = message;
            el.hidden = !message;
        }
    }
}

function esc( s )
{
    return String( s ).replace( /[&<>"']/g, ( c ) => ( { "&" : "&amp;", "<" : "&lt;", ">" : "&gt;", '"' : "&quot;", "'" : "&#39;" } )[ c ] );
}
